import { useCallback, useEffect, useRef, useState } from 'react';
import { approvalsApi } from '../../../shared/components/approvals/approvalsApi';

/*
  Loads card payloads for the current and the next item so flipping through the deck feels instant.
  Cards are keyed by document id; updates from a mutation replace the cached card.
*/
export function useCardCache(docType, currentId, nextId) {
  const cache = useRef(new Map());
  const [version, setVersion] = useState(0);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchCard = useCallback(
    async (id, { force = false } = {}) => {
      if (!id) return null;
      if (!force && cache.current.has(id)) return cache.current.get(id);
      const response = await approvalsApi.card(docType, id);
      if (response.ok) {
        cache.current.set(id, response.data);
        setVersion((v) => v + 1);
        return response.data;
      }
      return { error: response.error };
    },
    [docType]
  );

  useEffect(() => {
    cache.current.clear();
    setVersion((v) => v + 1);
  }, [docType]);

  useEffect(() => {
    let cancelled = false;
    if (!currentId) return undefined;
    if (!cache.current.has(currentId)) {
      setLoadingId(currentId);
      setError(null);
      fetchCard(currentId).then((card) => {
        if (cancelled) return;
        if (card?.error) setError(card.error);
        setLoadingId(null);
      });
    }
    if (nextId && nextId !== currentId && !cache.current.has(nextId)) fetchCard(nextId);
    return () => {
      cancelled = true;
    };
  }, [currentId, nextId, fetchCard]);

  const setCard = useCallback((card) => {
    if (!card?.document) return;
    const id = card.document[card.docType === 'creditnote' ? 'CreditnotaINvolgnummer' : 'faktuurinvolgnummer'];
    cache.current.set(id, card);
    setVersion((v) => v + 1);
  }, []);

  const invalidate = useCallback((id) => {
    cache.current.delete(id);
    setVersion((v) => v + 1);
  }, []);

  return {
    card: currentId ? cache.current.get(currentId) || null : null,
    nextCard: nextId ? cache.current.get(nextId) || null : null,
    loading: loadingId === currentId,
    error,
    setCard,
    invalidate,
    refresh: (id) => fetchCard(id, { force: true }),
    version
  };
}
